import { useCallback, useMemo, useState } from 'react';

import { useSetInterval } from '../useSetInterval';

import { useBrowserStorageValue } from './useBrowserStorageValue';

import type { BrowserStorageType } from './types';
import type { Jsonifiable } from 'type-fest';

interface UseStateWithBrowserStorageOptions {
  cookieAttributes?: Cookies.CookieAttributes;
  interval?: number;
}

export function useStateWithBrowserStorage<Value extends Jsonifiable>(
  initialValue: Value,
  storageKey: string,
  storageType: BrowserStorageType,
  { cookieAttributes, interval = 100 }: UseStateWithBrowserStorageOptions = {}
): [Value, (newState: Value | ((oldState: Value) => Value)) => void] {
  const [getBrowserStorageValue, setBrowserStorageValue] =
    useBrowserStorageValue(storageKey, storageType, {
      cookieAttributes,
    });

  const [jsonState, setJSONState] = useState<string>(
    getInitialStateValue(initialValue, getBrowserStorageValue())
  );

  const setStateFromBrowserStorage = useCallback(() => {
    const value = getBrowserStorageValue();
    if (value !== undefined) {
      setJSONState(value);
    }
  }, [getBrowserStorageValue]);

  // Browser storage => state
  useSetInterval(setStateFromBrowserStorage, interval);

  // State => browser storage
  const set = useCallback(
    (newValue: Value | ((oldState: Value) => Value)) => {
      if (typeof newValue === 'function') {
        setJSONState(oldJsonState => {
          const oldState = JSON.parse(oldJsonState) as Value;
          const newState = newValue(oldState);
          const newJsonState = JSON.stringify(newState);
          setBrowserStorageValue(newJsonState);
          return newJsonState;
        });
      } else {
        const newJsonValue = JSON.stringify(newValue);
        setJSONState(newJsonValue);
        setBrowserStorageValue(newJsonValue);
      }
    },
    [setBrowserStorageValue]
  );

  const parsedState = useMemo(
    () => JSON.parse(jsonState) as Value,
    [jsonState]
  );

  return [parsedState, set];
}

function getInitialStateValue<Value extends Jsonifiable>(
  initialValue: Value,
  browserStorageValue: string | undefined
): string {
  return browserStorageValue === undefined
    ? JSON.stringify(initialValue)
    : browserStorageValue;
}
